/* eslint-disable max-lines */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-danger */
import React, {
    useContext, useMemo, useEffect, useState,
} from "react";
import ProductReviews from "components/product/reviews";
import AffirmPayments from "components/affirm-payments";
import navigate from "@helpers/link-navigate";
import useImageGallery from "hooks/useImageGallery";
import useProduct from "hooks/useProduct";
import { CartContext } from "context/cart";
import {
    mapItems,
    trackAddToCart, trackQuantityChange, trackSliderClick, trackViewItem,
} from "analytics";
import YoutubeFrame from "components/youtube-frame";
import convertToEmbed from "helpers/youtube-embedded";
import StoreReviews from "components/store-reviews";
import getProductAttributes from "helpers/get-product-attributes";
import NProgress from "nprogress";
import PatioCalculator from "context/patio-calculator";
import extractGid from "helpers/extract-gid";
/* import RenderModules from "helpers/render-modules"; */
import Breadcrumbs from "@_components/breadcrumbs";
import hasContainer from "@root/helpers/has-container";
import { CategoryContext } from "@root/context/category";
import { FormValidateContext } from "@root/context/form-validate";
import useIsMobile from "@root/hooks/useIsMobile";
import { useLocation } from "@reach/router";
import ProductDetails from "./details";
import Patio from "./patio";
import ProductSummary from "../product-summary";
import ImageGalleryPatio from "../../_components/image-gallery";
import ImageGallery from "../image-gallery";
import CTA from "../../_components/cta";
import Tabs from "../../_components/tabs";
import { badgesByVehicles, manualReviews } from "../collection/data";
import ParsedContent from "./parsed-content";

const LoadingContainer = () => (
    <div className="container">
        <div className="container--other-pages">
            <h2 className="text-blue-700"> Loading Product... </h2>
        </div>
    </div>
);

const Details = ({ isPatio, product, handleVariantChange, quantity, handleQuantityChange, handleAddToCart, patioConfig, variantColors, version }) => {
    if (isPatio) {
        return (
            <Patio
                product={product}
                onVariantChange={handleVariantChange}
                quantity={quantity}
                handleQuantityChange={handleQuantityChange}
                onAddToCart={handleAddToCart}
                patioConfig={patioConfig}
                variantColors={variantColors}
            />
        );
    }
    return (
        <ProductDetails
            product={product}
            onVariantChange={handleVariantChange}
            quantity={quantity}
            handleQuantityChange={handleQuantityChange}
            onAddToCart={handleAddToCart}
            variantColors={variantColors}
            version={version}
        />
    );
};

function getSelectedOption(variant, option) {
    return variant?.selectedOptions?.find((selectedOption) => selectedOption.name === option)?.value;
}

const Product = ({ productData, patioConfig, variantColors, categorySlugMap }) => {
    const categories = productData?.product.categories || [];

    const currentLocation = useLocation();

    const breadcrumbItems = useMemo(() => {
        if (productData) {
            return [
                { title: "Home", url: "/" },
                ...categories.map((category) => ({
                    title: category.name,
                    url: categorySlugMap?.[category?.slug] ?? `${categorySlugMap?.[categories[0]?.slug]}/${category.slug}`,
                })),
                { title: productData?.product?.title, url: `/products/${productData?.product?.handle}` },
            ];
        }
        return [];
    }, [productData, categories, categorySlugMap]);

    if (!productData?.product) return <LoadingContainer />;

    const isPatio = productData?.product?.variants?.edges[0]?.node?.dynamic_price;

    const editData = currentLocation.state?.editData || {};
    const isEditing = editData.isEditing || false;
    const item = editData.item || null;
    const initialConfiguration = editData.configuration || {};
    const initialCustomAttributes = editData.customAttributes || {};

    // Preset variant should come as a query from getServerData, for now is disabled
    let presetVariantId;
    if (initialConfiguration?.variant) {
        presetVariantId = +initialConfiguration.variant;
    }

    const { validateForm } = useContext(FormValidateContext) || {};

    const editingQuantity = isEditing && editData.configuration.quantity ? editData.configuration.quantity : 1;

    const [quantity, setQuantity] = useState(editingQuantity);
    const { thumbsSlider, slideshowSlider, setThumbsSlider, setSlideshowSlider, slideTo } = useImageGallery();
    const { product, getVariantImageIndex, setVariant, getVariantByOptions } = useProduct(productData?.product, presetVariantId, isPatio);
    const { addCartItem, removeCartItem } = useContext(CartContext);

    const video = !isPatio && product?.metafields?.edges?.filter((metafield) => metafield.node.key === "full_video");
    const initialGallerySlide = (productData && productData.queryVariant && getVariantImageIndex(product.currentVariant) !== -1) ? getVariantImageIndex(product.currentVariant) : 0;

    const defaultCustomizations = product?.customizations?.map((customization) => customization?.default_variant_id).filter((ids) => ids);
    const defaultParameters = {};

    product?.measurements?.forEach((customization) => {
        defaultParameters[customization.key] = customization.default;
    });

    useEffect(() => {
        setTimeout(() => slideTo(0), 300);
    }, [getSelectedOption(product.currentVariant.node, "Fabric")]);

    const slideToSelectedVariantImage = async (variant) => {
        if (!isPatio) {
            const selectedVariantImageIndex = await getVariantImageIndex(variant);
            slideTo(selectedVariantImageIndex < 0 ? 0 : selectedVariantImageIndex);
        }
    };

    const handleVariantChange = (newVariantOption) => {
        const selectedVariant = getVariantByOptions(newVariantOption);
        setVariant(selectedVariant);
        slideToSelectedVariantImage(selectedVariant);
    };

    const handleAddToCart = async (qty, additionalItems = [], customAttributes = []) => {
        const isValid = validateForm();

        if (!isValid) return;

        const filteredItems = additionalItems.length > 0 ? additionalItems?.filter((item, index) => index === additionalItems.findLastIndex((obj) => item.variantId === obj.variantId)) : [];
        const hasFits = customAttributes.find((attribute) => attribute.key === "FITS");

        const baseCustomAttributes = [...customAttributes];

        if (!hasFits && !isPatio) baseCustomAttributes.push({ key: "FITS", value: productData.version || productData?.product?.productType || productData?.product?.handle });
        if (productData?.version) baseCustomAttributes.push({ key: "Product Extra", value: productData.version });

        // ToDo: We are just using mapItems to get the item_variant for adding it to the customAttributes, we should refactor this
        // This call also populates the item_metrics in the productData object.. so thats why we get the correct data below..
        const productAnalytics = mapItems([{
            ...product,
            variant: product?.currentVariant?.node,
            quantity: qty,
            customAttributes: baseCustomAttributes,
        }]);
        if (productAnalytics?.[0]?.item_variant) {
            baseCustomAttributes.push({
                key: "_item_variant",
                value: productAnalytics[0].item_variant,
            });
        }
        // ----------------------------

        if (productData?.product?.item_metrics) {
            const itemMetrics = productData?.product?.item_metrics;
            // eslint-disable-next-line no-loops/no-loops
            for (let i = 1; i <= 5; i += 1) {
                const num = i === 1 ? "" : i;
                if (itemMetrics[`item_category${num}`]) {
                    baseCustomAttributes.push({
                        key: `_item_category${num}`,
                        value: `${itemMetrics[`item_category${num}`]}`,
                    });
                }
            }
        }

        await addCartItem([{
            variantId: product?.currentVariant?.node?.id,
            quantity: qty,
            customAttributes: baseCustomAttributes,
        }, ...filteredItems]);

        trackAddToCart({
            ...product,
            variant: product?.currentVariant?.node,
            quantity: qty,
            customAttributes: baseCustomAttributes,
        }, { prevUrl: currentLocation?.state?.prevUrl });

        navigate("/cart");
    };

    const handleQuantityChange = (value) => {
        // trackQuantityChange({
        //     ...product, variant: product?.currentVariant?.node, quantity: value,
        // }, value);
        setQuantity(value);
    };

    useEffect(() => {
        trackViewItem({ ...product, variant: product?.currentVariant?.node }, { prevUrl: currentLocation?.state?.prevUrl });
        NProgress.done();
    }, [product?.currentVariant]);

    const isVariantImage = (image) => product?.variants?.edges.some((variant) => variant?.node?.image?.id === image.id);

    const WarrantyBadge = () => {
        const findVehicleBadges = badgesByVehicles.find((badge) => product?.title?.toLowerCase().includes(badge.type));
        if (!findVehicleBadges) return null;

        const findVehicleWarranty = findVehicleBadges.badges.find((vehicleBadge) => product.title.toLowerCase().includes(vehicleBadge.key));
        if (!findVehicleWarranty) return null;

        return findVehicleWarranty.Component;
    };

    const [manualReview, setManualReview] = useState(null);

    useEffect(() => {
        const byStyleAndSize = manualReviews.find((badge) => {
            const titleMatch = product?.title?.toLowerCase().includes(badge.title);
            const handleMatchOneOf = badge?.handles?.some((handle) => product?.handle?.toLowerCase().includes(handle));
            return titleMatch && handleMatchOneOf;
        });
        if (byStyleAndSize) {
            setManualReview(byStyleAndSize);
            return;
        }

        const vehicleByType = manualReviews.find((badge) => product?.title?.toLowerCase().includes(badge.type));
        if (!vehicleByType) return;

        const vehicleReviews = vehicleByType?.reviews?.find((vehicleBadge) => product?.title?.toLowerCase().includes(vehicleBadge.key));
        if (!vehicleReviews) return;

        setManualReview(vehicleReviews);
    }, [product]);

    const isBoat = product?.title.toLowerCase().includes("boat cover");
    const isPontoon = product?.title.toLowerCase().includes("pontoon covers");
    const isJetSki = product?.title.toLowerCase().includes("jet ski covers");
    const isCarSuvVanTruck = product?.title.toLowerCase().includes("car cover") || product?.title.toLowerCase().includes("suv cover") || product?.title.toLowerCase().includes("van cover") || product?.title.toLowerCase().includes("truck cover");

    const filterImagesByVariant = (images, selectedVariant) => {
        if (!isPatio || !images?.length) return images;

        const _variants = product?.variants?.edges;
        const variantImages = [];
        const addedFabrics = [];
        _variants.forEach((variant) => {
            const fabric = getSelectedOption(variant.node, "Fabric").toLowerCase();
            const fabricObject = {};

            if (!addedFabrics.includes(fabric)) {
                fabricObject.imageId = variant?.node?.image?.id;
                fabricObject.position = images.findIndex((image) => image.node.id === variant?.node?.image?.id);
                fabricObject.fabric = fabric;
                addedFabrics.push(fabric);
                variantImages.push(fabricObject);
            }
        });
        variantImages.sort((a, b) => a.position - b.position);

        const variantTitle = getSelectedOption(selectedVariant.node, "Fabric").toLowerCase();

        const selectedVariantIndex = variantImages.findIndex((image) => image.fabric === variantTitle);
        const selectedImage = variantImages[selectedVariantIndex];
        const startIndex = selectedImage.position;
        const endIndex = variantImages[selectedVariantIndex + 1] ? variantImages[selectedVariantIndex + 1].position - 1 : images.length - 1;

        const alwaysInclude = { startIndex: 0, endIndex: variantImages[0].position - 1 };

        const _selectedImages = images.filter((image, index) => {
            if (index >= alwaysInclude.startIndex && index <= alwaysInclude.endIndex) return true;
            return index >= startIndex && index <= endIndex;
        });

        return _selectedImages;
    };

    if (isPatio) {
        return (
            <div className="container py-4 bg-white">
                <div className="mb-4 md:mb-8">
                    <Breadcrumbs items={breadcrumbItems} />
                </div>

                <PatioCalculator
                    product={+extractGid(product?.id)}
                    variant={+extractGid(product?.currentVariant?.node?.id)}
                    parameters={defaultParameters}
                    customizations={defaultCustomizations}
                    initialConfiguration={initialConfiguration}
                    initialCustomAttributes={initialCustomAttributes}
                >

                    <div className="patio-product-page">
                        {useIsMobile() ? (
                            <div className="patio-product-page__title-mobile">
                                <h1 className="patio-product-page__title-main-mobile">{product?.title}</h1>
                                <span className="patio-product-page__title-stock-mobile">In Stock</span>
                            </div>
                        ) : (null)}
                        <div className="patio-product-page__image-gallery">
                            <ImageGalleryPatio
                                images={filterImagesByVariant(
                                    product?.images?.edges,
                                    product?.currentVariant,
                                ).map((edge) => ({
                                    src: edge.node.url,
                                    title: edge.node.altText,
                                }))}
                                thumbsSlider={thumbsSlider}
                                setThumbsSlider={setThumbsSlider}
                                slideshowSlider={slideshowSlider}
                                setSlideshowSlider={setSlideshowSlider}
                                slideTo={slideTo}
                                measurements={product?.measurements}
                            />
                        </div>

                        <div className="patio-product-page__details">
                            {!useIsMobile() ? (
                                <div className="patio-product-page__title-desktop">
                                    <h1 className="patio-product-page__title-main-desktop">{product?.title}</h1>
                                    <span className="patio-product-page__title-stock-desktop">In Stock</span>
                                </div>
                            ) : (null)}
                            <Details
                                isPatio={isPatio}
                                product={product}
                                handleVariantChange={handleVariantChange}
                                quantity={quantity}
                                handleQuantityChange={handleQuantityChange}
                                handleAddToCart={handleAddToCart}
                                version={productData?.version}
                                patioConfig={patioConfig}
                                variantColors={variantColors}
                                isEditing={isEditing}
                            />
                        </div>

                        <div className="patio-product-page__summary">
                            <ProductSummary
                                item={item}
                                product={product}
                                quantity={quantity}
                                handleQuantityChange={handleQuantityChange}
                                onAddToCart={handleAddToCart}
                                isEditing={isEditing}
                                removeCartItem={removeCartItem}
                            />
                        </div>

                    </div>

                </PatioCalculator>

                <div className="description-tabs">
                    <Tabs>
                        <div title="Description">
                            {product?.description && (
                                <div
                                    className="pdp-description py-4 text-[#444444]"
                                >
                                    <ParsedContent content={product?.description} />
                                </div>
                            )}
                        </div>
                        <div title="FAQ">
                            {product?.faq && (
                                <div
                                    className="pdp-description py-4 text-[#444444]"
                                    dangerouslySetInnerHTML={{ __html: product?.faq }}
                                />
                            )}
                        </div>
                        <div title="Reviews">
                            <StoreReviews className="!p-0" />
                        </div>
                    </Tabs>

                </div>

            </div>
        );
    }

    return (
        <>
            <div className="container py-4 bg-white">
                <div className="flex lg:flex-row flex-col pb-10 border-b">
                    <div className="flex w-full lg:w-1/2">
                        <ImageGallery
                            slideshow={product?.images?.edges}
                            thumbnails={product?.images?.edges}
                            thumbsSlider={thumbsSlider}
                            setThumbsSlider={setThumbsSlider}
                            slideshowSlider={slideshowSlider}
                            setSlideshowSlider={setSlideshowSlider}
                            slideTo={slideTo}
                            initialSlide={initialGallerySlide}
                            onAfterSlideChange={() => {
                                // This was not being sent to GA4 anyways.. why do we need it?
                                // trackSliderClick({ ...product })
                            }}
                            imageAlt={getProductAttributes(product)}
                            isVariantImage={isVariantImage}
                            WarrantyBadge={isBoat || isJetSki || isPontoon || isCarSuvVanTruck ? WarrantyBadge : null}
                        />
                    </div>

                    <div className="flex flex-col mt-5 lg:w-1/2 lg:pl-4 lg:m-0">
                        <Details
                            product={product}
                            handleVariantChange={handleVariantChange}
                            quantity={quantity}
                            handleQuantityChange={handleQuantityChange}
                            handleAddToCart={handleAddToCart}
                            version={productData?.version}
                            variantColors={variantColors}
                        />
                    </div>
                </div>
                {/* {video?.length > 0 && !isBoat && (
                    <div className="py-4 w-full border-b border-neutral-200 bg-white md:py-7">
                        <YoutubeFrame src={convertToEmbed(video[0].node.value)} className="w-full h-52 md:h-[22rem] lg:h-[28.25rem] xl:h-[40rem]" />
                    </div>
                )} */}

                {product?.description && <div className="pdp-description py-4 text-[#444444]" dangerouslySetInnerHTML={{ __html: product?.description }} />}
                {product?.bodyHtml && <div className="pdp-description py-4 text-[#444444]" dangerouslySetInnerHTML={{ __html: product?.bodyHtml }} />}
                {(["bow-boat-bimini-top", "bow-bimini-top", "bimini-top-replacement"].some((attribute) => product?.handle.toLowerCase().includes(attribute)) && product?.handle.toLowerCase().includes("fits")) || (product?.title.toLowerCase().includes("bimini top")) ? <img src="https://cdn.sanity.io/images/4swbd7aj/pre-prod/d81889ffb58811e7625a7f41b747a63ea83f40ce-1441x556.png" alt="Bimini Top Description" /> : null}

                {/* Added manual/hardcoded reviews */}
                {(product?.reviews && !manualReview) && <ProductReviews reviews={product?.reviews} />}
                {manualReview && <manualReview.Component {...manualReview.data} />}

            </div>
            <AffirmPayments price={product?.currentVariant?.node?.price} />
            <StoreReviews className="!p-0" />
        </>
    );
};

export default Product;
